<!--
  @component DatabaseAggregate — cross-project Database (scope = all / many).
  tier:   deployments composable-resource view (M:N)

  The Phase 2 centrepiece: the owner's databases as first-class resources, and
  the links to projects made visible + editable. Two views over the same model:
    • Cards   — one card per database, with its linked projects as chips.
    • Matrix  — a DB×project grid; click a cell to link (db.connect) or
                unlink (db.detach). This is "see and build what's connected".
  "+ New database" opens the create-and-share wizard.
  reads: useDeploymentIO (db.listOwned / db.listLinks / db.connect / db.detach),
         useIdentityIO (projects), useScopeIO (which projects are columns)
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { useScopeIO } from "./ScopeIO.svelte";
  import { scopeProjects } from "./fleet-util.js";
  import { initial, projectGradient } from "../../home/home-util.js";
  import DatabaseCreateWizard from "./DatabaseCreateWizard.svelte";

  const identity = useIdentityIO();
  const node = useDeploymentIO();
  const scope = useScopeIO();

  interface OwnedDb { id: string; originProjectId: string; engine: string; region: string; status: "provisioning" | "ready" | "failed" | "destroying" | "destroyed"; }
  interface Link { projectId: string; projectName: string; secretName: string; }

  // Columns = projects in scope. Rows = every database the owner has.
  const projects = $derived(scopeProjects(identity.projects, scope.projectIds));

  let owned = $state<OwnedDb[]>([]);
  let linkMap = $state<Record<string, Link[]>>({});
  let loaded = $state(false);
  let loadError = $state<string | null>(null);
  let cellBusy = $state<string>("");
  let view = $state<"cards" | "matrix">("cards");
  let wizardOpen = $state(false);

  async function reloadLinks(dbId: string): Promise<void> {
    try {
      const r = (await node.dispatch("db.listLinks", { databaseId: dbId })) as { links: Link[] };
      linkMap = { ...linkMap, [dbId]: r.links ?? [] };
    } catch { /* leave prior links in place */ }
  }
  async function loadAll(): Promise<void> {
    try {
      const o = (await node.dispatch("db.listOwned")) as OwnedDb[] ?? [];
      owned = o;
      const map: Record<string, Link[]> = {};
      await Promise.all(
        o.map(async (d) => {
          try { const r = (await node.dispatch("db.listLinks", { databaseId: d.id })) as { links: Link[] }; map[d.id] = r.links ?? []; }
          catch { map[d.id] = []; }
        }),
      );
      linkMap = map;
      loadError = null;
    } catch (e) {
      owned = [];
      loadError = e instanceof Error ? e.message : String(e);
    } finally {
      loaded = true;
    }
  }
  $effect(() => { void loadAll(); });

  function linksOf(dbId: string): Link[] { return linkMap[dbId] ?? []; }
  function isLinked(dbId: string, projId: string): boolean { return linksOf(dbId).some((l) => l.projectId === projId); }
  function secretFor(dbId: string, projId: string): string { return linksOf(dbId).find((l) => l.projectId === projId)?.secretName ?? "DATABASE_URL"; }
  function nameOf(projId: string): string { return identity.projects.find((p) => p.id === projId)?.name ?? projId; }
  function dbLabel(db: OwnedDb): string { return nameOf(db.originProjectId); }

  async function toggle(dbId: string, projId: string): Promise<void> {
    if (cellBusy) return; // serialize toggles — keeps the grid honest
    const key = `${dbId}:${projId}`;
    cellBusy = key;
    try {
      if (isLinked(dbId, projId)) {
        await node.dispatch("db.detach", { target: { id: projId }, options: { secretName: secretFor(dbId, projId) } });
      } else {
        await node.dispatch("db.connect", { target: { id: projId }, databaseId: dbId });
      }
      await reloadLinks(dbId);
      loadError = null;
    } catch (e) {
      loadError = e instanceof Error ? e.message : String(e);
    } finally {
      cellBusy = "";
    }
  }
</script>

<div class="agg">
  <header>
    <div class="title">
      <h1>Databases</h1>
      <p class="lede">Managed Postgres as shared resources — one database can back several projects. Link or unlink a project to inject (or drop) its <code>DATABASE_URL</code> on the next deploy.</p>
    </div>
    <div class="tools">
      <div class="seg" role="tablist" aria-label="View">
        <button type="button" role="tab" aria-selected={view === "cards"} class:on={view === "cards"} onclick={() => (view = "cards")}>Cards</button>
        <button type="button" role="tab" aria-selected={view === "matrix"} class:on={view === "matrix"} onclick={() => (view = "matrix")}>Matrix</button>
      </div>
      <button type="button" class="btn-primary" onclick={() => (wizardOpen = true)}>+ New database</button>
    </div>
  </header>

  {#if loadError}<p class="err">{loadError}</p>{/if}

  {#if !loaded}
    <p class="muted">Loading databases…</p>
  {:else if owned.length === 0}
    <div class="empty-card">
      <p class="empty-title">No databases yet</p>
      <p class="empty-help">Create a managed Postgres database and share it across any of your projects.</p>
      <button type="button" class="btn-primary" onclick={() => (wizardOpen = true)}>+ New database</button>
    </div>
  {:else if view === "cards"}
    <div class="cards">
      {#each owned as db (db.id)}
        {@const links = linksOf(db.id)}
        <section class="dbcard">
          <div class="card-head">
            <span class="mark" style="background: {projectGradient(db.originProjectId)}">{initial(dbLabel(db))}</span>
            <div class="card-id">
              <span class="card-name" title={dbLabel(db)}>{dbLabel(db)}</span>
              <span class="card-meta">{db.engine} · {db.region}</span>
            </div>
            <span class="pill {db.status}">{db.status === "ready" ? "Ready" : db.status === "provisioning" ? "Provisioning" : db.status === "failed" ? "Failed" : db.status}</span>
          </div>
          <div class="used">
            <span class="used-label">Used by {links.length} project{links.length === 1 ? "" : "s"}</span>
            {#if links.length > 0}
              <div class="chips">
                {#each links as l (l.projectId)}<span class="chip">{l.projectName}</span>{/each}
              </div>
            {:else}
              <span class="muted">Not linked to any project yet — switch to Matrix to link one.</span>
            {/if}
          </div>
        </section>
      {/each}
    </div>
  {:else}
    {#if projects.length === 0}
      <p class="muted">No projects in scope. Widen the scope to link databases.</p>
    {:else}
      <div class="matrix-wrap">
        <table class="matrix">
          <thead>
            <tr>
              <th class="corner">database \ project</th>
              {#each projects as p (p.id)}
                <th class="col" title={p.name}><span class="cmark" style="background: {projectGradient(p.id)}">{initial(p.name)}</span></th>
              {/each}
            </tr>
          </thead>
          <tbody>
            {#each owned as db (db.id)}
              <tr>
                <th class="rowlabel" title={dbLabel(db)}>
                  <span class="mark sm" style="background: {projectGradient(db.originProjectId)}">{initial(dbLabel(db))}</span>
                  <span class="rl-name">{dbLabel(db)}</span>
                </th>
                {#each projects as p (p.id)}
                  {@const on = isLinked(db.id, p.id)}
                  {@const busy = cellBusy === `${db.id}:${p.id}`}
                  <td>
                    <button
                      type="button"
                      class="cell"
                      class:on={on}
                      class:busy={busy}
                      disabled={cellBusy !== "" && !busy}
                      title={`${on ? "Unlink" : "Link"} ${dbLabel(db)} ↔ ${p.name}`}
                      aria-pressed={on}
                      onclick={() => void toggle(db.id, p.id)}
                    >
                      <span class="dot"></span>
                    </button>
                  </td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <p class="legend"><span class="dot on"></span> linked &nbsp;·&nbsp; <span class="dot"></span> not linked &nbsp;·&nbsp; click a cell to toggle</p>
    {/if}
  {/if}
</div>

<DatabaseCreateWizard open={wizardOpen} onClose={() => (wizardOpen = false)} onCreated={() => void loadAll()} defaultHostId={scope.singleId} />

<style>
  .agg { display: flex; flex-direction: column; gap: var(--space-4); max-width: 1180px; font-family: var(--type-font); }
  header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap; }
  .title { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }
  h1 { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .lede { margin: 0; font-size: 13.5px; color: var(--text-secondary); line-height: 1.55; max-width: 640px; }
  .lede code { font-family: var(--type-mono); font-size: 12px; }
  .tools { display: flex; align-items: center; gap: var(--space-2); flex-shrink: 0; }
  .muted { font-size: 13px; color: var(--text-tertiary); }
  .err { margin: 0; font-size: 12.5px; color: var(--error); }

  .seg { display: inline-flex; border: 0.5px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .seg button { appearance: none; background: transparent; border: 0; padding: 6px var(--space-3); font: inherit; font-size: 12.5px; color: var(--text-secondary); cursor: pointer; transition: background var(--ease-fast); }
  .seg button.on { background: var(--bg-active); color: var(--text-primary); }
  .seg button:not(.on):hover { background: var(--bg-hover); }

  .btn-primary { appearance: none; border-radius: var(--radius-md); padding: 0 var(--space-4); height: 34px; background: var(--bg-active); color: var(--text-primary); border: 0.5px solid var(--border-strong); font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; white-space: nowrap; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .btn-primary:hover { background: var(--bg-selected); border-color: var(--border-focus); }

  .empty-card { display: flex; flex-direction: column; align-items: flex-start; gap: var(--space-2); padding: var(--space-6); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-xl, 18px); }
  .empty-title { margin: 0; font-size: 15px; font-weight: 600; color: var(--text-primary); }
  .empty-help { margin: 0 0 var(--space-2); font-size: 13px; color: var(--text-secondary); }

  /* Cards */
  .cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: var(--space-3); }
  .dbcard { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-4); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-lg, 14px); }
  .card-head { display: flex; align-items: center; gap: var(--space-2); }
  .mark { width: 26px; height: 26px; border-radius: var(--radius-sm); flex-shrink: 0; display: grid; place-items: center; font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.92); }
  .mark.sm { width: 20px; height: 20px; font-size: 10px; }
  .card-id { display: flex; flex-direction: column; min-width: 0; flex: 1; }
  .card-name { font-size: 14px; font-weight: 600; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .card-meta { font-size: 11.5px; color: var(--text-tertiary); font-family: var(--type-mono); }
  .pill { font-size: 11px; font-weight: 500; padding: 2px var(--space-2); border-radius: var(--radius-pill); background: var(--bg-hover); color: var(--text-tertiary); white-space: nowrap; }
  .pill.ready { background: color-mix(in srgb, var(--success) 16%, transparent); color: var(--success); }
  .pill.provisioning { background: color-mix(in srgb, var(--warning) 16%, transparent); color: var(--warning); }
  .pill.failed { background: color-mix(in srgb, var(--error) 16%, transparent); color: var(--error); }
  .used { display: flex; flex-direction: column; gap: var(--space-2); }
  .used-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-tertiary); }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .chip { font-size: 12px; padding: 3px var(--space-2); border-radius: var(--radius-pill); background: var(--bg-hover); color: var(--text-secondary); border: 0.5px solid var(--border-subtle); }

  /* Matrix */
  .matrix-wrap { overflow-x: auto; border: 0.5px solid var(--border); border-radius: var(--radius-lg, 14px); background: var(--glass-fill); scrollbar-width: thin; }
  .matrix { border-collapse: collapse; width: 100%; }
  .matrix th, .matrix td { padding: 0; }
  .corner { position: sticky; left: 0; z-index: 2; background: var(--glass-fill); text-align: left; padding: var(--space-2) var(--space-3); font-size: 10.5px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-ghost); white-space: nowrap; }
  .col { padding: var(--space-2) 6px; }
  .cmark { width: 22px; height: 22px; border-radius: var(--radius-sm); display: inline-grid; place-items: center; font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.92); }
  .rowlabel { position: sticky; left: 0; z-index: 1; background: var(--glass-fill); display: flex; align-items: center; gap: var(--space-2); padding: var(--space-2) var(--space-3); border-top: 0.5px solid var(--border-subtle); max-width: 220px; }
  .rl-name { font-size: 13px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .matrix tbody tr td { border-top: 0.5px solid var(--border-subtle); text-align: center; }
  .cell { appearance: none; background: transparent; border: 0; width: 100%; height: 38px; display: grid; place-items: center; cursor: pointer; transition: background var(--ease-fast); }
  .cell:hover:not(:disabled) { background: var(--bg-hover); }
  .cell:disabled { cursor: not-allowed; }
  .cell .dot { width: 13px; height: 13px; border-radius: var(--radius-pill); border: 1.5px solid var(--border-strong); background: transparent; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .cell.on .dot { background: var(--accent, #5b8cff); border-color: var(--accent, #5b8cff); }
  .cell.busy .dot { opacity: 0.5; }
  .legend { display: flex; align-items: center; gap: 4px; margin: 0; font-size: 11.5px; color: var(--text-tertiary); }
  .legend .dot { width: 11px; height: 11px; border-radius: var(--radius-pill); border: 1.5px solid var(--border-strong); display: inline-block; vertical-align: middle; }
  .legend .dot.on { background: var(--accent, #5b8cff); border-color: var(--accent, #5b8cff); }
</style>
